import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Edit as EditIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { collection, doc, getDocs, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { DEFAULT_NOTIFICATION_TEMPLATES } from "../data/defaultNotificationTemplates";
import {
  getTemplatePreviewValues,
  renderTemplateString,
} from "../utils/emailTemplateUtils";

const formatUpdatedAt = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value?.toDate === "function") {
    return value.toDate().toLocaleString();
  }

  if (value instanceof Date) {
    return value.toLocaleString();
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleString();
};

const normalizeStoredTemplates = (snapshot) => {
  const items = {};

  snapshot.forEach((templateDoc) => {
    items[templateDoc.id] = {
      id: templateDoc.id,
      ...templateDoc.data(),
    };
  });

  return items;
};

const EXCLUDED_TEMPLATE_KEYS = new Set([
  "APP_UPDATE",
  "OTP",
  "BOOKING",
  "BOOKING_REJECTED",
  "LEAD",
  "PROVIDER",
  "PROVIDER_PROFILE_UPDATE",
  "PROVIDER_DOCUMENTS_UPDATE",
]);

const NotificationTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [formData, setFormData] = useState({
    title: "",
    body: "",
  });

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError("");
      const storedSnapshot = await getDocs(collection(db, "notificationTemplates"));
      const storedTemplates = normalizeStoredTemplates(storedSnapshot);
      const systemTemplates = DEFAULT_NOTIFICATION_TEMPLATES;

      const knownKeys = new Set(
        systemTemplates.map((template) => template.key),
      );

      const mergedSystemTemplates = systemTemplates.map((template) => {
        const stored =
          storedTemplates[template.key] ||
          storedTemplates[template.templateKey];
        const title = stored?.title || template.title || "";
        const body = stored?.body || template.body || "";

        return {
          ...template,
          id: template.key,
          key: template.key,
          title,
          body,
          defaultTitle: template.title || "",
          defaultBody: template.body || "",
          updatedAt: stored?.updatedAt || null,
          customized:
            title !== (template.title || "") ||
            body !== (template.body || ""),
          isCustom: false,
        };
      }).filter((template) => !EXCLUDED_TEMPLATE_KEYS.has(String(template.key || "").toUpperCase()));

      const customTemplates = Object.values(storedTemplates)
        .filter((template) => !EXCLUDED_TEMPLATE_KEYS.has(String(template.id || "").toUpperCase()))
        .filter((template) => !knownKeys.has(template.id))
        .map((template) => ({
          id: template.id,
          key: template.id,
          name: template.name || template.id,
          category: template.category || "Custom",
          description: template.description || "Custom push notification template",
          variables: Array.isArray(template.variables)
            ? template.variables
            : [],
          title: template.title || "",
          body: template.body || "",
          defaultTitle: template.title || "",
          defaultBody: template.body || "",
          updatedAt: template.updatedAt || null,
          customized: true,
          isCustom: true,
        }));

      const mergedTemplates = [
        ...mergedSystemTemplates,
        ...customTemplates,
      ].sort((left, right) => {
        const categoryCompare = String(left.category).localeCompare(
          String(right.category),
        );
        return categoryCompare !== 0
          ? categoryCompare
          : String(left.name).localeCompare(String(right.name));
      });

      setTemplates(mergedTemplates);
    } catch (err) {
      console.error("Error fetching notification templates:", err);
      setError("Failed to load notification templates. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const categories = useMemo(() => {
    const uniqueCategories = [
      ...new Set(templates.map((template) => template.category)),
    ];
    return ["All", ...uniqueCategories];
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const matchesCategory =
        categoryFilter === "All" || template.category === categoryFilter;
      const matchesSearch =
        !searchTerm ||
        [
          template.name,
          template.category,
          template.description,
          template.title,
          template.key,
        ]
          .join(" ")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      return matchesCategory && matchesSearch;
    });
  }, [categoryFilter, searchTerm, templates]);

  const previewValues = useMemo(() => {
    return editingTemplate ? getTemplatePreviewValues(editingTemplate) : {};
  }, [editingTemplate]);

  const renderedPreviewTitle = useMemo(() => {
    return renderTemplateString(formData.title, previewValues);
  }, [formData.title, previewValues]);

  const renderedPreviewBody = useMemo(() => {
    return renderTemplateString(formData.body, previewValues);
  }, [formData.body, previewValues]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      title: template.title || "",
      body: template.body || "",
    });
    setDialogOpen(true);
  };

  const handleRestoreDefaults = () => {
    if (!editingTemplate) {
      return;
    }

    setFormData({
      title: editingTemplate.defaultTitle || "",
      body: editingTemplate.defaultBody || "",
    });
  };

  const updateTemplateLocally = (templateKey, nextValues) => {
    setTemplates((currentTemplates) =>
      currentTemplates.map((template) => {
        if (template.key !== templateKey) {
          return template;
        }

        return {
          ...template,
          ...nextValues,
          customized:
            (nextValues.title ?? template.title) !==
              template.defaultTitle ||
            (nextValues.body ?? template.body) !== template.defaultBody,
        };
      }),
    );
  };

  const handleSave = async () => {
    if (!editingTemplate) {
      return;
    }

    if (!formData.title.trim() || !formData.body.trim()) {
      showSnackbar("Title and body are required.", "error");
      return;
    }

    try {
      setSaving(true);

      const updatedTemplate = {
        templateKey: editingTemplate.key,
        name: editingTemplate.name,
        category: editingTemplate.category,
        description: editingTemplate.description,
        variables: editingTemplate.variables || [],
        title: formData.title,
        body: formData.body,
        system: !editingTemplate.isCustom,
        updatedAt: new Date(),
      };

      await setDoc(
        doc(db, "notificationTemplates", editingTemplate.key),
        updatedTemplate,
        { merge: true },
      );

      updateTemplateLocally(editingTemplate.key, {
        title: formData.title,
        body: formData.body,
        updatedAt: updatedTemplate.updatedAt,
      });

      setDialogOpen(false);
      showSnackbar("Notification template saved successfully.");
    } catch (err) {
      console.error("Error saving notification template:", err);
      showSnackbar("Failed to save notification template.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleResetTemplate = async (template) => {
    try {
      setSaving(true);

      const resetTemplate = {
        templateKey: template.key,
        name: template.name,
        category: template.category,
        description: template.description,
        variables: template.variables || [],
        title: template.defaultTitle,
        body: template.defaultBody,
        system: !template.isCustom,
        updatedAt: new Date(),
      };

      await setDoc(
        doc(db, "notificationTemplates", template.key),
        resetTemplate,
        { merge: true },
      );

      updateTemplateLocally(template.key, {
        title: template.defaultTitle,
        body: template.defaultBody,
        updatedAt: resetTemplate.updatedAt,
      });

      if (editingTemplate?.key === template.key) {
        setFormData({
          title: template.defaultTitle,
          body: template.defaultBody,
        });
      }

      showSnackbar("Template restored to default content.", "info");
    } catch (err) {
      console.error("Error resetting notification template:", err);
      showSnackbar("Failed to restore template defaults.", "error");
    } finally {
      setSaving(false);
    }
  };

  const editorPlaceholders = editingTemplate
    ? [...(editingTemplate.variables || [])]
    : [];

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        These templates are applied by Cloud Functions when push notifications are sent. Changes here affect future notifications without a new app release.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Search templates"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by name, category, key, or title"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            select
            fullWidth
            label="Category"
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            SelectProps={{ native: true }}
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTemplates}
            disabled={loading}
            fullWidth
            sx={{ height: "100%" }}
          >
            Refresh
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 4 }}>
          <CircularProgress size={24} />
          <Typography>Loading notification templates...</Typography>
        </Box>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center">
              No templates matched your filters.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredTemplates.map((template) => (
            <Grid key={template.key} size={{ xs: 12, lg: 6 }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mb: 1, flexWrap: "wrap" }}
                  >
                    <Chip
                      label={template.category}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={template.customized ? "Customized" : "Default"}
                      size="small"
                      color={template.customized ? "success" : "default"}
                    />
                    {template.isCustom && (
                      <Chip
                        label="Custom Record"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                  </Stack>

                  <Typography variant="h6" gutterBottom>
                    {template.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {template.description}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 1 }}
                  >
                    Key: {template.key}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Title:</strong> {template.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      display: "-webkit-box",
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      mb: 2,
                    }}
                  >
                    {template.body}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ flexWrap: "wrap", mb: 1 }}
                  >
                    {(template.variables || []).slice(0, 6).map((variable) => (
                      <Chip
                        key={variable}
                        label={`{{${variable}}}`}
                        size="small"
                      />
                    ))}
                    {(template.variables || []).length > 6 && (
                      <Chip
                        label={`+${template.variables.length - 6} more`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>

                  {template.updatedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Last saved: {formatUpdatedAt(template.updatedAt)}
                    </Typography>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEdit(template)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => handleResetTemplate(template)}
                    disabled={saving || !template.customized}
                  >
                    Restore Default
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {editingTemplate?.name || "Edit Notification Template"}
        </DialogTitle>
        <DialogContent>
          {editingTemplate && (
            <Grid container spacing={3} sx={{ pt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <TextField
                    label="Template Key"
                    value={editingTemplate.key}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    label="Push Title"
                    value={formData.title}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        title: event.target.value,
                      }))
                    }
                    fullWidth
                  />
                  <TextField
                    label="Push Body"
                    value={formData.body}
                    onChange={(event) =>
                      setFormData((current) => ({
                        ...current,
                        body: event.target.value,
                      }))
                    }
                    multiline
                    rows={14}
                    fullWidth
                    helperText="Use placeholders like {{providerName}} or {{bookingId}} where available."
                  />

                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Available Placeholders
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ flexWrap: "wrap" }}
                    >
                      {editorPlaceholders.map((placeholder) => (
                        <Chip
                          key={placeholder}
                          label={`{{${placeholder}}}`}
                          size="small"
                        />
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Preview
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Preview uses sample values for placeholders.
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 2 }}>
                      Title: {renderedPreviewTitle}
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: "#fafafa",
                        minHeight: 280,
                        overflow: "auto",
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {renderedPreviewBody}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRestoreDefaults} disabled={!editingTemplate}>
            Load Default
          </Button>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save Template"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationTemplates;
